import { useMemo } from "react";
import { formatValue } from "../../lib/kernelFormat.js";
import { boundedLimit, boundedRows, linearPercent } from "../../lib/decisionScales.js";
import { renderInline } from "../Markdown.jsx";
import { KernelEmpty } from "./shared.jsx";

export const kind = "brief";

const SEVERITIES = {
    critical: { tone: "var(--severity-critical)", label: "critical" },
    warning: { tone: "var(--severity-high)", label: "warning" },
    positive: { tone: "var(--green)", label: "positive" },
    neutral: { tone: "var(--muted)", label: "note" },
};

function severityOf(value) {
    return SEVERITIES[value] || SEVERITIES.neutral;
}

// The large summary slot is sized for a headline number. Models also put
// identifiers there, such as a session id, which read as text after the label.
function isHeadlineNumber(value) {
    if (typeof value === "number") return Number.isFinite(value);
    return typeof value === "string" && /^-?\d+(?:\.\d+)?$/.test(value.trim());
}

export function Component({ spec, data, fillHeight = false, onFocus, linkifyText }) {
    const format = spec.format || "number";
    const maxItems = boundedLimit(spec.maxItems, 8, 12);
    const findings = useMemo(
        () => boundedRows(Array.isArray(data?.findings) ? data.findings.filter(Boolean) : [], maxItems),
        [data, maxItems],
    );
    const summary = data?.summary || null;
    const disclosure = data?.disclosure || null;
    if (!summary && !findings.length && !disclosure) {
        return <KernelEmpty message={spec.empty || "No findings to show"} />;
    }

    const evidence = spec.evidence || "bar";
    const impactPeak = findings.reduce((peak, finding) => (
        Math.max(peak, Math.abs(Number(finding?.impact) || 0))
    ), 0);
    const summaryFormat = summary?.format || format;
    const inline = (text) => (text == null ? null : renderInline(String(text), { linkifyText }));

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? (
                <div className="mb-[8px] text-[12px] font-semibold leading-[1.35] text-[color:var(--ink)]">{inline(spec.title)}</div>
            ) : null}
            {summary ? (
                <div className="border-b border-[color:var(--line)] pb-[9px]">
                    {isHeadlineNumber(summary.value) ? (
                        <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[2px]">
                            <span className="font-mono text-[24px] leading-none tabular-nums text-[color:var(--ink)]">
                                {formatValue(summary.value, summaryFormat)}
                            </span>
                            {summary.label ? <span className="text-[11px] text-[color:var(--muted)]">{summary.label}</span> : null}
                        </div>
                    ) : summary.label || summary.value != null ? (
                        <div className="text-[12px] leading-[1.4] text-[color:var(--ink)]">
                            {summary.label ? (
                                <span className="text-[color:var(--muted)]">
                                    {summary.value != null ? `${summary.label}: ` : summary.label}
                                </span>
                            ) : null}
                            {summary.value != null ? inline(formatValue(summary.value, "text")) : null}
                        </div>
                    ) : null}
                    {summary.detail ? (
                        <div className="mt-[4px] text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]">{inline(summary.detail)}</div>
                    ) : null}
                </div>
            ) : null}
            <div className="divide-y divide-[color:var(--line)]">
                {findings.map((finding, index) => {
                    const severity = severityOf(finding.severity);
                    const focus = finding.focus;
                    const clickable = Boolean(onFocus && focus?.field && focus?.value != null);
                    const select = () => onFocus(focus.field, focus.value);
                    return (
                        <div
                            key={finding.id || `${finding.headline || "finding"}-${index}`}
                            role={clickable ? "button" : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            onClick={clickable ? select : undefined}
                            onKeyDown={clickable ? (event) => {
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                select();
                            } : undefined}
                            className={`py-[8px]${clickable ? " cursor-pointer" : ""}`}
                        >
                            <div className="flex gap-[9px]">
                                <span
                                    role="img"
                                    aria-label={severity.label}
                                    className="mt-[5px] h-[7px] w-[7px] shrink-0"
                                    style={{ backgroundColor: severity.tone }}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12px] leading-[1.4] text-[color:var(--ink)]">{inline(finding.headline)}</div>
                                    {finding.detail ? (
                                        <div className="mt-[2px] text-[11px] leading-[1.45] text-[color:var(--muted)]">{inline(finding.detail)}</div>
                                    ) : null}
                                </div>
                                {finding.impact != null ? (
                                    <span className="shrink-0 font-mono text-[10.5px] tabular-nums text-[color:var(--muted-soft)]">
                                        {formatValue(finding.impact, finding.format || format)}
                                    </span>
                                ) : null}
                            </div>
                            {evidence === "bar" && impactPeak > 0 && finding.impact != null ? (
                                <div className="mt-[5px] h-[6px] w-full bg-[color:var(--line-soft)]">
                                    <div
                                        className="h-full"
                                        style={{
                                            width: `${linearPercent(Math.abs(Number(finding.impact) || 0), impactPeak)}%`,
                                            backgroundColor: severity.tone,
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {disclosure ? (
                <div className="mt-[8px] text-[10.5px] leading-[1.4] text-[color:var(--muted)]">
                    {inline(typeof disclosure === "string" ? disclosure : disclosure.text)}
                </div>
            ) : null}
        </div>
    );
}
